import { useState, useRef } from "react";
import { toast } from "sonner";
import { Download, Upload, ChevronLeft, ShieldCheck, AlertTriangle, Loader2, KeyRound, Fingerprint, Cloud, RefreshCw, ExternalLink } from "lucide-react";
import { driveConfigured, requestDriveToken, uploadEncryptedBackup, listVaultBackups, downloadBackup } from "../../lib/vault/driveBackup";
import { markVaultBackupNow } from "./BackupReminderBanner";

/**
 * BackupPanel — encrypted vault backup export + restore + Google Drive sync.
 * Backup file (.tpvault.json) is ALREADY encrypted (contains only ciphertext).
 * To restore on another device, the user needs the SAME PIN that created it.
 */
export default function BackupPanel({ vault, onBack }) {
  const [busy, setBusy] = useState(false);
  const [restorePin, setRestorePin] = useState("");
  const [pendingImport, setPendingImport] = useState(null);
  const [driveFiles, setDriveFiles] = useState(null); // null=not-listed, []=empty, [x…]=ready
  const [driveBusy, setDriveBusy] = useState(false);
  const fileRef = useRef();

  const _bundleJson = async () => JSON.stringify(await vault.exportBackup(), null, 2);

  const exportBackup = async () => {
    setBusy(true);
    try {
      const json = await _bundleJson();
      const blob = new Blob([json], { type: "application/json" });
      const link = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 10);
      link.href = URL.createObjectURL(blob);
      link.download = `travelerpulse-vault-backup-${stamp}.tpvault.json`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(link.href);
      markVaultBackupNow();
      toast.success("Encrypted backup downloaded");
    } catch (e) { toast.error(e.message || "Export failed"); }
    setBusy(false);
  };

  const chooseFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) return toast.error("Backup file is unexpectedly large (>20MB)");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const bundle = JSON.parse(reader.result);
        if (bundle.format !== "tp-private-vault-backup") return toast.error("This isn't a valid vault backup file");
        setPendingImport(bundle);
      } catch { toast.error("Could not read backup file"); }
    };
    reader.readAsText(f);
  };

  const doImport = async () => {
    if (!pendingImport || !restorePin) return;
    if (!window.confirm("Import will REPLACE the current vault on this device with the backup contents. Continue?")) return;
    setBusy(true);
    try {
      const r = await vault.importBackup(pendingImport, restorePin);
      toast.success(`Restored ${r.imported} compartment${r.imported === 1 ? "" : "s"}`);
      setPendingImport(null);
      setRestorePin("");
      onBack && onBack();
    } catch (e) { toast.error(e.message || "Import failed — check your PIN"); }
    setBusy(false);
  };

  // ------ Google Drive ------
  const driveOn = driveConfigured();

  const driveBackupNow = async () => {
    setDriveBusy(true);
    try {
      const token = await requestDriveToken({ prompt: "" });
      const json = await _bundleJson();
      const file = await uploadEncryptedBackup(token, json);
      markVaultBackupNow();
      toast.success(`Uploaded to Drive · ${file.name}`);
      // Refresh listing if it's open
      if (driveFiles !== null) refreshDrive(token);
    } catch (e) { toast.error(e.message || "Drive backup failed"); }
    setDriveBusy(false);
  };

  const refreshDrive = async (existingToken) => {
    setDriveBusy(true);
    try {
      const token = existingToken || await requestDriveToken({ prompt: "" });
      const files = await listVaultBackups(token);
      setDriveFiles(files);
    } catch (e) { toast.error(e.message || "Drive list failed"); }
    setDriveBusy(false);
  };

  const driveRestore = async (fileId) => {
    const restorePinPrompt = window.prompt("Enter the PIN that was active when this backup was made:");
    if (!restorePinPrompt) return;
    if (!window.confirm("Restore will REPLACE the current vault on this device. Continue?")) return;
    setDriveBusy(true);
    try {
      const token = await requestDriveToken({ prompt: "" });
      const text = await downloadBackup(token, fileId);
      const bundle = JSON.parse(text);
      if (bundle.format !== "tp-private-vault-backup") throw new Error("Not a valid vault backup");
      const r = await vault.importBackup(bundle, restorePinPrompt);
      toast.success(`Restored ${r.imported} compartment${r.imported === 1 ? "" : "s"} from Drive`);
      onBack && onBack();
    } catch (e) { toast.error(e.message || "Drive restore failed — check your PIN"); }
    setDriveBusy(false);
  };

  return (
    <div className="max-w-md mx-auto p-4" data-testid="backup-panel">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-emerald-700 inline-flex items-center gap-1 mb-3"><ChevronLeft className="w-4 h-4" /> Back</button>

      <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded p-4 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-indigo-600 grid place-items-center"><ShieldCheck className="w-6 h-6 text-white" /></div>
          <div>
            <h1 className="font-serif font-900 text-xl">Cross-device backup</h1>
            <p className="text-[11px] text-slate-500">Encrypted file · restore anywhere with your PIN</p>
          </div>
        </div>
      </div>

      <div className="border border-slate-200 rounded p-4 mb-4">
        <h3 className="font-heading font-800 text-sm mb-2 inline-flex items-center gap-1.5"><Download className="w-4 h-4 text-emerald-600" /> Export</h3>
        <p className="text-xs text-slate-600 mb-3 leading-snug">Downloads a <code>.tpvault.json</code> file. All contents are already encrypted with your PIN — safe to email to yourself or drop on iCloud/Google Drive.</p>
        <button onClick={exportBackup} disabled={busy} data-testid="backup-export"
          className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-700 rounded inline-flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download encrypted backup
        </button>
      </div>

      {/* --- Google Drive backup --- */}
      <div className="border border-slate-200 rounded p-4 mb-4">
        <h3 className="font-heading font-800 text-sm mb-2 inline-flex items-center gap-1.5"><Cloud className="w-4 h-4 text-sky-600" /> Google Drive</h3>
        {!driveOn ? (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-700 leading-snug" data-testid="drive-not-configured">
            Drive backup isn't set up on this deployment yet. The site owner needs to:
            <ol className="list-decimal ml-4 mt-1 space-y-0.5">
              <li>Create an OAuth 2.0 Client ID (Web) in <a className="text-blue-700 hover:underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console <ExternalLink className="w-3 h-3 inline" /></a></li>
              <li>Add <code>https://travelerpulse.pro</code> to Authorized JavaScript origins</li>
              <li>Enable the Drive API and add <code>drive.file</code> scope</li>
              <li>Paste the Client ID into <code>REACT_APP_GOOGLE_DRIVE_CLIENT_ID</code> in the platform Secrets tab</li>
            </ol>
          </div>
        ) : (
          <>
            <p className="text-xs text-slate-600 mb-3 leading-snug">Encrypted backup is uploaded to your own Drive (app-only folder — TravelerPulse can only see files it created).</p>
            <div className="flex gap-2 mb-3">
              <button onClick={driveBackupNow} disabled={driveBusy} data-testid="drive-backup-now"
                className="flex-1 py-2.5 bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white text-sm font-700 rounded inline-flex items-center justify-center gap-1.5">
                {driveBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />} Backup now
              </button>
              <button onClick={() => refreshDrive()} disabled={driveBusy} data-testid="drive-list"
                className="py-2.5 px-3 border-2 border-sky-300 hover:border-sky-500 text-sky-700 text-sm font-700 rounded inline-flex items-center justify-center gap-1.5">
                <RefreshCw className={`w-4 h-4 ${driveBusy ? "animate-spin" : ""}`} /> {driveFiles === null ? "List backups" : "Refresh"}
              </button>
            </div>
            {driveFiles !== null && (
              <div className="border border-sky-100 rounded overflow-hidden" data-testid="drive-file-list">
                {driveFiles.length === 0 ? (
                  <div className="p-3 text-xs text-slate-500 text-center">No Drive backups yet</div>
                ) : driveFiles.map((f) => (
                  <div key={f.id} className="flex items-center justify-between p-2.5 border-b border-sky-50 last:border-b-0 hover:bg-sky-50/50">
                    <div className="min-w-0">
                      <div className="text-xs font-600 text-slate-800 truncate">{f.name}</div>
                      <div className="text-[10px] text-slate-500">{new Date(f.createdTime).toLocaleString()} · {(Number(f.size || 0)/1024).toFixed(1)}KB</div>
                    </div>
                    <button onClick={() => driveRestore(f.id)} disabled={driveBusy} data-testid={`drive-restore-${f.id}`}
                      className="ml-2 shrink-0 text-[11px] font-700 px-2.5 py-1 border border-sky-400 text-sky-700 hover:bg-sky-100 rounded inline-flex items-center gap-1">
                      <KeyRound className="w-3 h-3" /> Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="border border-slate-200 rounded p-4 mb-4">
        <h3 className="font-heading font-800 text-sm mb-2 inline-flex items-center gap-1.5"><Upload className="w-4 h-4 text-indigo-600" /> Restore from file</h3>
        <p className="text-xs text-slate-600 mb-3 leading-snug">Pick a <code>.tpvault.json</code> backup file, then enter the PIN that created it. Existing vault data on this device will be replaced.</p>
        <input ref={fileRef} type="file" accept=".json,application/json" onChange={chooseFile} className="hidden" data-testid="backup-file-input" />
        <button onClick={() => fileRef.current?.click()} data-testid="backup-choose-file"
          className="w-full py-2.5 border-2 border-slate-300 hover:border-indigo-500 text-slate-700 text-sm font-700 rounded inline-flex items-center justify-center gap-1.5 mb-3">
          <Upload className="w-4 h-4" /> Choose backup file
        </button>

        {pendingImport && (
          <div className="p-3 bg-indigo-50 border border-indigo-200 rounded space-y-2" data-testid="backup-pending">
            <div className="text-xs text-indigo-900">
              Backup from <b>{new Date(pendingImport.exportedAt).toLocaleString()}</b> · {Object.keys(pendingImport.records || {}).length} compartments
            </div>
            <label className="block text-sm">
              <span className="font-600">Backup PIN</span>
              <input type="password" autoFocus value={restorePin} onChange={(e) => setRestorePin(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && doImport()}
                data-testid="backup-restore-pin"
                className="mt-1 w-full border-2 border-indigo-300 focus:border-indigo-500 px-3 py-2 text-lg font-mono tracking-wider text-center rounded" />
            </label>
            <button onClick={doImport} disabled={busy || !restorePin} data-testid="backup-restore"
              className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white text-sm font-700 rounded inline-flex items-center justify-center gap-1.5">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} <KeyRound className="w-4 h-4" /> Restore vault
            </button>
          </div>
        )}
      </div>

      <div className="p-3 bg-amber-50 border border-amber-300 rounded text-[11px] text-amber-900 flex items-start gap-2">
        <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span><b>Multi-device tip:</b> After restore, re-enroll biometric <Fingerprint className="w-3 h-3 inline" /> on this device — biometric wraps are always device-local.</span>
      </div>
    </div>
  );
}
